import { CircularProgress, Stack, styled } from '@mui/material'
import { ReactElement, ReactNode } from 'react'
import { Modal } from 'ComponentLibrary/src/piazza/modals'
import { Tab, TabBar } from 'ComponentLibrary/src/piazza/selectors'
import { TextButtonNew } from 'ComponentLibrary/src/piazza/buttons'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { UserSettingsModalFormState } from 'ComponentLibrary/src/molecules/UserSettings/types'

export type UserSettingsModalTab<T extends string> = {
    value: T
    label: string
    icon: ReactElement
    disabled?: boolean
}

type UserSettingsModalShellProps<T extends string> = {
    isOpen: boolean
    title: string
    tabs: UserSettingsModalTab<T>[]
    selectedTab: T
    onTabChange: (tab: T) => void
    onClose: () => void
    onCancel: () => void
    closeIconTooltip?: string
    cancelLabel: string
    saveLabel: string
    activeFormState?: UserSettingsModalFormState
    children: ReactNode
}

export function UserSettingsModalShell<T extends string>({
    isOpen,
    title,
    tabs,
    selectedTab,
    onTabChange,
    onClose,
    onCancel,
    closeIconTooltip,
    cancelLabel,
    saveLabel,
    activeFormState,
    children,
}: UserSettingsModalShellProps<T>) {
    const isPersisting = !!activeFormState?.isPersisting
    const isSaveDisabled = !activeFormState?.isDirty || isPersisting

    return (
        <Modal
            title={title}
            isOpen={isOpen}
            onClose={onClose}
            showCloseIcon
            closeIconTooltip={closeIconTooltip}
            width="800px"
            height="800px"
            removeContentPadding
        >
            <Stack flex={1} minWidth={0} minHeight={0} alignSelf="stretch" overflow="hidden">
                <TabBarWrapper>
                    <TabBar selectedTab={selectedTab} handleChange={onTabChange} ariaLabel={title}>
                        {tabs.map((tab) => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                label={tab.label}
                                icon={tab.icon}
                                disabled={tab.disabled}
                                customHeight="40px"
                            />
                        ))}
                    </TabBar>
                </TabBarWrapper>
                <BodyWrapper>{children}</BodyWrapper>
                <FooterWrapper direction="row">
                    <TextButtonNew label={cancelLabel} onClick={onCancel} variant="secondary" disabled={isPersisting} />
                    <TextButtonNew
                        label={saveLabel}
                        onClick={activeFormState?.onSave ?? (() => undefined)}
                        variant="primary"
                        disabled={isSaveDisabled}
                        leftIcon={
                            activeFormState?.isPersisting ? (
                                <CircularProgress
                                    thickness={4}
                                    size={tokens.SizingIconS}
                                    sx={{ color: tokens.ColorIconDisabled, position: 'relative', left: 'initial', top: 'initial' }}
                                />
                            ) : undefined
                        }
                    />
                </FooterWrapper>
            </Stack>
        </Modal>
    )
}

const TabBarWrapper = styled(Stack)`
    width: 100%;
    padding: ${tokens.SizingSpacing100} ${tokens.SizingSpacingNone} ${tokens.SizingSpacingNone};
    border-bottom: ${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault};
    box-shadow: ${tokens.Flyout.boxShadow};
    background: ${tokens.ColorSurfaceDefault};
    box-sizing: border-box;
    z-index: 2;

    && .MuiTabs-root {
        width: 100%;
        padding: 0 ${tokens.SizingSpacing300};
        margin-bottom: -${tokens.SizingBorderM};
        box-sizing: border-box;
    }

    && .MuiTabs-flexContainer {
        border-bottom: 0;
    }
`

const BodyWrapper = styled(Stack)`
    flex: 1;
    min-height: 0;
    width: 100%;
    overflow-y: scroll;
    background: ${tokens.ColorSurfaceDefault};
`

const FooterWrapper = styled(Stack)`
    width: 100%;
    padding: ${tokens.SizingSpacing150} ${tokens.SizingSpacing300};
    align-items: center;
    justify-content: flex-end;
    gap: ${tokens.SizingSpacing150};
    border-top: ${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault};
    background: ${tokens.ColorSurfaceDefault};
    box-sizing: border-box;
`
